/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Frontend-local, CSP-safe reimplementation of
 * `SST.extractRequiredFieldsFromSchema` (`@product-passport/shared/util/schema`).
 *
 * `SST` uses AJV internally, which compiles schemas via runtime code generation
 * (`new Function`). This is blocked by the browser's Content-Security-Policy
 * (no `'unsafe-eval'`), causing `SST`'s method to throw when called from the
 * webapp. `@cfworker/json-schema` is a pure-JS JSON Schema validator with no
 * runtime code generation, making it safe to use under a strict browser CSP.
 *
 * This class replicates the traversal logic of `SST.extractRequiredFieldsFromSchema`
 * exactly (including the pre-existing behaviour of not descending into `else`
 * branches — see `collectPathsFromAllOf`), swapping only the single point where
 * AJV was used to evaluate an `allOf` entry's `if` condition against live data.
 *
 * Implementation Notes:
 * - Only the boolean `.valid` result of `@cfworker/json-schema`'s `Validator` is
 *   consumed — no mapping of its `OutputUnit`/`errors` array to AJV's error shape
 *   is required, since `SST.collectPathsFromAllOf` (the method being mirrored)
 *   never reads AJV's `.errors` either.
 * - The `'2020-12'` draft is used explicitly for every `Validator` instantiation,
 *   matching the `$schema` declaration of every schema definition in
 *   `data/schemadefinitions/*.json`.
 * - This class is **not** used by, and does not replace, any backend usage of
 *   `SST`. The backend keeps using AJV via `SST` unchanged.
 *
 * Related Modules:
 * - {@link PassportService.getRequiredFieldPaths} — sole consumer of this class.
 * - `SST` (`libs/shared/util/schema/src/lib/sst.ts`) — the backend counterpart
 *   whose `extractRequiredFieldsFromSchema` traversal logic is mirrored here.
 */

import {JSONSchema} from '@apidevtools/json-schema-ref-parser';
import {Validator} from '@cfworker/json-schema';

/**
 * Reimplements `SST.extractRequiredFieldsFromSchema` using `@cfworker/json-schema`
 * instead of AJV, so it can run under a strict browser Content-Security-Policy.
 */
export class RequiredFieldPathExtractor {
  /**
   * Extracts all required field paths from an already-resolved in-memory JSON Schema object.
   *
   * Walks `required`/`properties`, array `minItems`, and `allOf` blocks. For `if/then`
   * entries inside `allOf`, the `if` condition is evaluated against the provided `data`
   * object using `@cfworker/json-schema`, and only the matching `then` block's required
   * fields are included in the total.
   *
   * @param {JSONSchema} schema - The fully-resolved JSON Schema object.
   * @param {unknown} [data] - The data object being evaluated. Required for conditional
   *   `if/then` evaluation; when omitted, conditional fields are not counted.
   * @returns {string[]} A flat list of required field paths (e.g. `["/section/field"]`).
   * @example
   * extractRequiredFieldsFromSchema({
   *   type: 'object',
   *   required: ['section'],
   *   properties: { section: { required: ['name'], properties: { name: { type: 'string' } } } },
   * })
   * // → ['/section/name']
   */
  public extractRequiredFieldsFromSchema(
    schema: JSONSchema,
    data?: unknown,
  ): string[] {
    const allPaths = this.collectRequiredPaths(schema, '', false, data);
    return allPaths.filter((path) => path !== '');
  }

  /**
   * Recursively collects required field paths from a JSON Schema node.
   *
   * Mirrors `SST.collectRequiredPaths`: delegates to
   * {@link collectPathsFromRequired}, {@link collectPathsFromAllOf}, and
   * {@link collectPathsFromProperties}. When `walkProperties` is false and no
   * fields were found at this node, the node itself is treated as a required
   * leaf and its own path is returned.
   *
   * @param {JSONSchema} obj - The schema node to inspect.
   * @param {string} currentPath - The slash-delimited path accumulated so far.
   * @param {boolean} walkProperties - When true, the traversal is inside a `then`-schema
   *   and must descend into every property regardless of whether `required` is present.
   * @param {unknown} [data] - Live passport data used to evaluate `if` conditions.
   * @returns {string[]} All required field paths found at or below this node.
   */
  private collectRequiredPaths(
    obj: JSONSchema,
    currentPath: string,
    walkProperties: boolean,
    data?: unknown,
  ): string[] {
    // An array schema with a minItems constraint is represented by its single items
    // schema. Descend into the first array element (index 0) to find its required fields.
    if (obj?.type === 'array' && (obj.minItems ?? 0) > 0) {
      return this.collectRequiredPaths(
        obj.items as JSONSchema,
        `${currentPath}/0`,
        walkProperties,
        data,
      );
    }

    const fields: string[] = [];

    // Track which property keys were already handled by the required block so that
    // the walkProperties pass below does not count the same field twice.
    const coveredByRequired = new Set<string>();

    const requiredFields = this.collectPathsFromRequired(
      obj,
      currentPath,
      coveredByRequired,
      data,
    );
    fields.push(...requiredFields);

    const allOfFields = this.collectPathsFromAllOf(obj, currentPath, data);
    fields.push(...allOfFields);

    if (walkProperties) {
      const propertyFields = this.collectPathsFromProperties(
        obj,
        currentPath,
        coveredByRequired,
        data,
      );
      fields.push(...propertyFields);
    }

    // In normal mode (not inside a then-schema) a node that produced no child paths
    // is itself a required leaf — return its own path as the single result.
    if (fields.length === 0 && !walkProperties) {
      return [currentPath];
    }

    return fields;
  }

  /**
   * Collects required field paths declared by the `required` keyword on a schema node.
   *
   * @param {JSONSchema} obj - The schema node that may carry a `required` keyword.
   * @param {string} currentPath - The slash-delimited path to this node.
   * @param {Set<string>} coveredByRequired - Mutable set; property names are added here
   *   so the caller can avoid processing them again during the properties walk.
   * @param {unknown} [data] - Live passport data forwarded to recursive calls.
   * @returns {string[]} Paths for all fields declared required at this node.
   */
  private collectPathsFromRequired(
    obj: JSONSchema,
    currentPath: string,
    coveredByRequired: Set<string>,
    data?: unknown,
  ): string[] {
    if (!obj?.required) {
      return [];
    }

    let requiredKeys: string[];

    if (typeof obj.required === 'boolean') {
      // When required is a boolean true, every property on the object is required.
      requiredKeys = Object.keys(obj.properties ?? {});
    } else {
      requiredKeys = obj.required;
    }

    requiredKeys.forEach((key) => {
      coveredByRequired.add(key);
    });

    return requiredKeys.flatMap((key) => {
      const propertySchema = (obj.properties?.[key] ?? {}) as JSONSchema;
      return this.collectRequiredPaths(
        propertySchema,
        `${currentPath}/${key}`,
        false,
        data,
      );
    });
  }

  /**
   * Collects required field paths from every entry in an `allOf` array.
   *
   * For plain `allOf` entries (no `if`/`then` pair) the entry is always traversed.
   * For conditional entries that carry both `if` and `then`, the `if` schema is
   * evaluated against the live data using `@cfworker/json-schema`'s `Validator`.
   * Only when the condition passes are the `then`-schema's paths included in the
   * result. `else` branches are intentionally never collected — this mirrors a
   * pre-existing `SST` characteristic that must be preserved identically.
   *
   * When `data` is `undefined` the method silently skips all conditional entries.
   *
   * @param {JSONSchema} obj - The schema node that may carry an `allOf` keyword.
   * @param {string} currentPath - The slash-delimited path to this node.
   * @param {unknown} [data] - Live passport data used to evaluate each `if` condition.
   * @returns {string[]} Paths contributed by matched `allOf` entries.
   */
  private collectPathsFromAllOf(
    obj: JSONSchema,
    currentPath: string,
    data?: unknown,
  ): string[] {
    const allOf = (obj as {allOf?: unknown[]}).allOf;

    if (!Array.isArray(allOf)) {
      return [];
    }

    return (allOf as JSONSchema[]).flatMap((subSchema) =>
      this.collectPathsFromAllOfEntry(subSchema, currentPath, data),
    );
  }

  private collectPathsFromAllOfEntry(
    subSchema: JSONSchema,
    currentPath: string,
    data?: unknown,
  ): string[] {
    const ifSchema = (subSchema as {if?: JSONSchema}).if;
    const thenSchema = (subSchema as {then?: JSONSchema}).then;

    if (!ifSchema || !thenSchema) {
      // Plain allOf entry with no condition — always include its required fields.
      return this.collectRequiredPaths(subSchema, currentPath, false, data);
    }

    // Only evaluate the condition when live data is available.
    if (data === undefined) {
      return [];
    }

    // Strip the $schema declaration for consistency/safety, mirroring SST's
    // defensive behaviour (the draft is passed explicitly below anyway).
    const ifSchemaStripped = {...(ifSchema as Record<string, unknown>)};
    delete ifSchemaStripped['$schema'];

    const conditionMatches = new Validator(
      ifSchemaStripped,
      '2020-12',
    ).validate(data).valid;

    if (!conditionMatches) {
      return [];
    }

    // then-schemas nest required inside properties, so walkProperties=true is
    // required to discover constraints beyond the top level of the then object.
    return this.collectRequiredPaths(thenSchema, currentPath, true, data);
  }

  /**
   * Collects required field paths by descending into every property of a schema node.
   *
   * Only called when `walkProperties` is active (inside `then`-schemas). Skips any
   * property already accounted for by the `required` block (tracked in
   * `coveredByRequired`) to avoid double-counting.
   *
   * @param {JSONSchema} obj - The schema node whose properties should be walked.
   * @param {string} currentPath - The slash-delimited path to this node.
   * @param {Set<string>} coveredByRequired - Property names already handled by a
   *   `required` block at this level; these are skipped.
   * @param {unknown} [data] - Live passport data forwarded to recursive calls.
   * @returns {string[]} Paths found by descending into uncovered properties.
   */
  private collectPathsFromProperties(
    obj: JSONSchema,
    currentPath: string,
    coveredByRequired: Set<string>,
    data?: unknown,
  ): string[] {
    if (!obj?.properties) {
      return [];
    }

    const fields: string[] = [];

    for (const [key, propertySchema] of Object.entries(
      obj.properties as Record<string, JSONSchema>,
    )) {
      if (!coveredByRequired.has(key)) {
        fields.push(
          ...this.collectRequiredPaths(
            propertySchema,
            `${currentPath}/${key}`,
            true,
            data,
          ),
        );
      }
    }

    return fields;
  }
}
